// #1201 · Cuánto tarda «Proponer» del calendario, del clic a la propuesta pintada.
//
// Dirección midió unos 90 s con DSB (semana 44) cuando el solver corta a los 20 s, y nadie sabía dónde
// se iba el resto. La API devuelve en `tiempos` lo que tarda cada fase en el servidor; aquí se mide lo
// que solo ve el navegador —lanzar, esperar el aviso de fin, descargar el resultado y repintar— y se
// escribe todo junto en la consola, en una línea que se puede copiar tal cual en la incidencia.
//
// Es SOLO medida: no pinta nada ni cambia el flujo. Por eso cada función acepta un cronómetro nulo o
// de otra pasada sin quejarse: medir nunca puede romper un Proponer.

function ahora() {
  return (typeof performance !== 'undefined' && performance && typeof performance.now === 'function')
    ? performance.now()
    : Date.now();
}

/** Cronómetro de una corrida lanzada desde el botón: empieza en el clic. */
function empezar() {
  return { pasadaId: null, marcas: { clic: ahora() } };
}

/** La API admitió la pasada: a partir de aquí el cronómetro es de esa pasada. */
function lanzada(crono, pasadaId) {
  if (!crono || !crono.marcas) return;
  crono.pasadaId = pasadaId || null;
  marcar(crono, 'lanzada');
}

/**
 * El cronómetro que corresponde a `pasadaId`. Si el que hay es de otra pasada —un reenganche tras F5,
 * o una pasada que lanzó otra pestaña— se empieza uno sin clic: se mide lo que queda y el total no se
 * da, porque no se sabe cuándo empezó.
 */
function dePasada(crono, pasadaId) {
  if (crono && crono.marcas && crono.pasadaId === pasadaId) return crono;
  return { pasadaId: pasadaId || null, marcas: {} };
}

/** Apunta el instante de `nombre` la primera vez; las siguientes no lo mueven. */
function marcar(crono, nombre) {
  if (!crono || !crono.marcas || crono.marcas[nombre] != null) return;
  crono.marcas[nombre] = ahora();
}

function tramo(marcas, desde, hasta) {
  const a = marcas[desde];
  const b = marcas[hasta];
  return (a == null || b == null) ? null : Math.round(b - a);
}

function num(v) {
  return (typeof v === 'number' && isFinite(v)) ? v : null;
}

/**
 * Las fases en el orden en que pasan, en milisegundos; `null` es una fase que no se pudo medir. El
 * «aviso de fin» es lo que tarda en llegar al navegador el evento FIN una vez el servidor terminó
 * (red + canal SSE): sale de restar al tramo medido aquí lo que el servidor dice que trabajó.
 */
function fases(crono, servidor) {
  const m = (crono && crono.marcas) || {};
  const s = servidor || {};
  const esperaFin = tramo(m, 'lanzada', 'fin');
  const trabajoServidor = num(s.totalMs) == null ? null : num(s.totalMs) + (num(s.colaMs) || 0);
  return [
    { fase: 'lanzar (clic → pasada admitida)', ms: tramo(m, 'clic', 'lanzada') },
    { fase: 'servidor · cola', ms: num(s.colaMs) },
    { fase: 'servidor · construir petición', ms: num(s.construirMs) },
    { fase: '   · coordenadas (geo)', ms: num(s.construirGeoMs) },
    { fase: '   · recuentos [SOLVER-ESTADO]', ms: num(s.construirConteoEstadoMs) },
    { fase: '   · candidatas de campo', ms: num(s.construirCampoMs) },
    { fase: '   · informes', ms: num(s.construirInformesMs) },
    { fase: '   · inspector y agenda fija', ms: num(s.construirInspectorMs) },
    { fase: '   · matriz de distancias', ms: num(s.construirMatrizMs) },
    { fase: 'servidor · solver (ida y vuelta)', ms: num(s.solverMs) },
    { fase: '   · motor (lo que dice el solver)', ms: num(s.solverMotorMs) },
    { fase: '   · transporte (red, cola, JSON)', ms: num(s.solverTransporteMs) },
    { fase: 'servidor · mapear respuesta', ms: num(s.mapearMs) },
    { fase: 'servidor · esperar bloqueadas por negocio', ms: num(s.bloqueadosNegocioEsperaMs) },
    { fase: '   · (la consulta, en paralelo al solver)', ms: num(s.bloqueadosNegocioMs) },
    { fase: 'servidor · registro de la corrida', ms: num(s.registroMs) },
    { fase: 'aviso de fin (red + SSE)', ms: (esperaFin == null || trabajoServidor == null) ? null : esperaFin - trabajoServidor },
    { fase: 'descargar resultado', ms: tramo(m, 'fin', 'resultado') },
    { fase: 'aplicar propuesta', ms: tramo(m, 'resultado', 'aplicada') },
    { fase: 'repintar (recarga de agenda incluida)', ms: tramo(m, 'aplicada', 'pintada') },
    { fase: 'TOTAL (clic → pintada)', ms: tramo(m, 'clic', 'pintada') }
  ];
}

function seg(ms) {
  return ms == null ? '—' : (ms / 1000).toFixed(1).replace('.', ',') + ' s';
}

/** Una línea legible con todas las fases. */
function texto(crono, servidor) {
  return '[PROPONER-TIEMPOS] pasada=' + ((crono && crono.pasadaId) || '—') + ' · '
    + fases(crono, servidor).map(function (f) { return f.fase.trim() + ' ' + seg(f.ms); }).join(' · ');
}

/** A la consola: la línea para copiar y, debajo, la misma tabla para leerla. */
function trazar(crono, servidor) {
  if (typeof console === 'undefined') return;
  try {
    console.info(texto(crono, servidor));
    if (typeof console.table === 'function') console.table(fases(crono, servidor));
  } catch (e) {
    // Medir no puede romper la pantalla.
  }
}

export { empezar, lanzada, dePasada, marcar, fases, texto, trazar };
export default { empezar, lanzada, dePasada, marcar, fases, texto, trazar };
